/**
 * Shared rules for listing status chips on property cards and related surfaces.
 * UI components stay in components/; this file is the single source for predicates.
 */

import {
  isClosedListingStatus,
  isComingSoonListingStatus,
} from "@/utils/marketStatus"
import { isAfter } from "date-fns"

import type { ClosedStatusLabel } from "@/components/cms/tenants/tenantStatusBannerMaps"

const MS_PER_MINUTE = 60 * 1000
const MS_PER_DAY = 24 * 60 * 60 * 1000

/** How long “fresh listing” / relative time-on-market chips stay eligible (from `OnMarketDate`). */
export const LISTING_FRESH_BADGE_MAX_DAYS = 7

/** How long a price-reduction chip stays visible when `PriceChangeTimestamp` is present. */
export const PRICE_CUT_BADGE_MAX_DAYS = 14

export type OpenHouseLike = {
  OpenHouseStartTime?: string | null
  OpenHouseEndTime?: string | null
  OpenHouseType?: string | null
  OpenHouseStatus?: string | null
  /** When explicitly `false`, exclude from consumer banners (matches LDP `preparePropertyData`). */
  MlgCanView?: boolean | null
}

export type GetOpenHouseVisibilityCutoffOptions = {
  propertyTimeZoneName?: string | null
  /** When end is missing, keep the chip visible through end of the start date in listing TZ. */
  assumeEndOfLocalDayWhenEndMissing?: boolean
}

function filterConsumerOpenHouses(
  openHouses: OpenHouseLike[] | null | undefined
): OpenHouseLike[] {
  if (!openHouses?.length) return []
  return openHouses.filter((oh) => oh.MlgCanView !== false)
}

/** Last millisecond of the local calendar day containing `instant` in `timeZone`. */
export function getEndOfLocalDayFromInstant(
  instant: Date,
  timeZone: string | null | undefined
): Date | null {
  if (Number.isNaN(instant.getTime())) return null
  const tz = timeZone || "America/Chicago"
  const minutesFromMidnight = getListingLocalMinutesSinceMidnight(instant, tz)
  const msUntilEndOfDay =
    (24 * 60 - minutesFromMidnight) * MS_PER_MINUTE - 1
  if (msUntilEndOfDay < 0) return null
  return new Date(instant.getTime() + msUntilEndOfDay)
}

function isOpenHouseCanceled(oh: OpenHouseLike): boolean {
  return (
    oh.OpenHouseStatus === "Canceled" ||
    oh.OpenHouseType === "Canceled"
  )
}

/** Last instant the open house should still appear (end time, else start). */
export function getOpenHouseVisibilityCutoff(
  openHouse: Pick<OpenHouseLike, "OpenHouseStartTime" | "OpenHouseEndTime">,
  options?: GetOpenHouseVisibilityCutoffOptions
): Date | null {
  const end = openHouse.OpenHouseEndTime?.trim()
    ? new Date(openHouse.OpenHouseEndTime)
    : null
  if (end && !Number.isNaN(end.getTime())) return end

  const start = openHouse.OpenHouseStartTime?.trim()
    ? new Date(openHouse.OpenHouseStartTime)
    : null
  if (start && !Number.isNaN(start.getTime())) {
    if (options?.assumeEndOfLocalDayWhenEndMissing) {
      return getEndOfLocalDayFromInstant(start, options.propertyTimeZoneName)
    }
    return start
  }

  return null
}

/** Non-canceled open house that has not ended yet (upcoming or in progress). */
export function isOpenHouseActiveOrUpcoming(
  openHouse: OpenHouseLike,
  now: Date,
  options?: GetOpenHouseVisibilityCutoffOptions
): boolean {
  if (isOpenHouseCanceled(openHouse)) return false
  const cutoff = getOpenHouseVisibilityCutoff(openHouse, options)
  return Boolean(cutoff && isAfter(cutoff, now))
}

/**
 * Active or upcoming non-canceled open houses, earliest start first.
 *
 * Always treats a missing end time as end-of-local-day so that an in-progress
 * open house without an OpenHouseEndTime is not hidden after its start time
 * passes (mirrors the computed-field fallback in hasUpcomingOpenHouseFromEarliestStart).
 */
function listFutureOpenHousesSortedForBanner(
  openHouses: OpenHouseLike[] | null | undefined,
  now: Date,
  options?: GetOpenHouseVisibilityCutoffOptions
): OpenHouseLike[] {
  if (!openHouses?.length) return []
  const opts: GetOpenHouseVisibilityCutoffOptions = {
    assumeEndOfLocalDayWhenEndMissing: true,
    ...options,
  }
  return filterConsumerOpenHouses(openHouses)
    .filter((oh) => isOpenHouseActiveOrUpcoming(oh, now, opts))
    .sort(
      (a, b) =>
        new Date(a.OpenHouseStartTime || 0).getTime() -
        new Date(b.OpenHouseStartTime || 0).getTime()
    )
}

export function countFutureOpenHousesForBanner(
  openHouses: OpenHouseLike[] | null | undefined,
  now: Date,
  options?: GetOpenHouseVisibilityCutoffOptions
): number {
  return listFutureOpenHousesSortedForBanner(openHouses, now, options).length
}

/**
 * Next upcoming non-canceled open house (earliest future `OpenHouseStartTime`).
 */
export function getNextFutureOpenHouseForBanner(
  openHouses: OpenHouseLike[] | null | undefined,
  now: Date,
  options?: GetOpenHouseVisibilityCutoffOptions
): OpenHouseLike | null {
  const sorted = listFutureOpenHousesSortedForBanner(openHouses, now, options)
  return sorted[0] ?? null
}


type OpenHouseTimeParts = {
  hour12: string
  minute: number
  period: "am" | "pm"
}

function getOpenHouseTimePartsInZone(
  date: Date,
  timeZone: string
): OpenHouseTimeParts | null {
  if (Number.isNaN(date.getTime())) return null
  const tz = timeZone || "America/Chicago"
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
  let hour12 = ""
  let minute = 0
  let dayPeriod = ""
  for (const p of dtf.formatToParts(date)) {
    if (p.type === "hour") hour12 = p.value
    if (p.type === "minute") minute = Number.parseInt(p.value, 10) || 0
    if (p.type === "dayPeriod") dayPeriod = p.value.toLowerCase()
  }
  if (!hour12 || (dayPeriod !== "am" && dayPeriod !== "pm")) return null
  return { hour12, minute, period: dayPeriod }
}

/**
 * Compact 12h time in a zone, e.g. `5:30pm` or `7pm` (drops `:00` minutes).
 */
export function formatOpenHouseTimeSegment(date: Date, timeZone: string): string {
  const tz = timeZone || "America/Chicago"
  const parts = getOpenHouseTimePartsInZone(date, tz)
  if (!parts) return ""
  return formatOpenHouseTimeFromParts(parts, { includeMeridiem: true })
}

function formatOpenHouseTimeFromParts(
  parts: OpenHouseTimeParts,
  options: { includeMeridiem: boolean }
): string {
  const minuteChunk =
    parts.minute === 0 ? "" : `:${String(parts.minute).padStart(2, "0")}`
  const core = `${parts.hour12}${minuteChunk}`
  if (!options.includeMeridiem) return core
  return `${core}${parts.period}`
}

/** Minutes since local midnight in `timeZone` (0–1439), using 24h wall clock. */
function getListingLocalMinutesSinceMidnight(date: Date, timeZone: string): number {
  const tz = timeZone || "America/Chicago"
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hour: "numeric",
    hourCycle: "h23",
    minute: "2-digit",
  })
  let hour24 = 0
  let minute = 0
  for (const p of dtf.formatToParts(date)) {
    if (p.type === "hour") hour24 = Number.parseInt(p.value, 10) || 0
    if (p.type === "minute") minute = Number.parseInt(p.value, 10) || 0
  }
  return hour24 * 60 + minute
}

export type FormatOpenHouseBannerLabelOptions = {
  /** Additional future open houses after the first; rendered as ` +n` after `(M/D)`. */
  additionalFutureOpenHouseCount?: number
}

/**
 * Single-line open house label: `Fri 5:30-7pm (6/12)` (door icon is rendered by the caller).
 * Abbreviated weekday; when both times are strictly before noon or both at/after noon, only the
 * end shows am/pm. When the window crosses noon (morning into afternoon), both times include
 * meridiem (e.g. `11:30am-3pm`, `11:30am-12pm`).
 * With more than one future open house: `… (6/12) +2`.
 */
export function formatOpenHouseBannerLabel(
  startIso: string,
  endIso: string,
  propertyTimeZoneName: string | null | undefined,
  options?: FormatOpenHouseBannerLabelOptions
): string {
  const tz = propertyTimeZoneName || "America/Chicago"
  const start = new Date(startIso)
  const end = new Date(endIso)
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return ""

  const weekday = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    weekday: "short",
  }).format(start)

  const md = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "numeric",
    day: "numeric",
  }).format(start)

  const startParts = getOpenHouseTimePartsInZone(start, tz)
  const endParts = getOpenHouseTimePartsInZone(end, tz)
  if (!startParts || !endParts) return ""

  const noonMinutes = 12 * 60
  const startM = getListingLocalMinutesSinceMidnight(start, tz)
  const endM = getListingLocalMinutesSinceMidnight(end, tz)
  const bothStrictlyBeforeNoon = startM < noonMinutes && endM < noonMinutes
  const bothAtOrAfterNoon = startM >= noonMinutes && endM >= noonMinutes
  const omitMeridiemOnStart = bothStrictlyBeforeNoon || bothAtOrAfterNoon

  const startT = formatOpenHouseTimeFromParts(startParts, {
    includeMeridiem: !omitMeridiemOnStart,
  })
  const endT = formatOpenHouseTimeFromParts(endParts, { includeMeridiem: true })
  const extra =
    typeof options?.additionalFutureOpenHouseCount === "number" &&
    options.additionalFutureOpenHouseCount > 0
      ? ` +${options.additionalFutureOpenHouseCount}`
      : ""
  return `${weekday} ${startT}-${endT} (${md})${extra}`
}

/**
 * Relative label from an ISO instant vs `now` (min / hr / days ago).
 * Used for “fresh listing” recency; eligibility is handled separately (e.g. `OnMarketDate` + `LISTING_FRESH_BADGE_MAX_DAYS`).
 */
export function formatFreshListingRelativeLabel(
  anchorIso: string | null | undefined,
  now: Date
): string {
  if (!anchorIso) return ""
  const anchor = new Date(anchorIso)
  if (Number.isNaN(anchor.getTime())) return ""
  const deltaMs = now.getTime() - anchor.getTime()
  if (deltaMs < 0) return "0 min ago"
  const totalMinutes = Math.floor(deltaMs / MS_PER_MINUTE)
  if (totalMinutes < 60) return `${totalMinutes} min ago`
  if (totalMinutes < 24 * 60) return `${Math.floor(totalMinutes / 60)}hr ago`
  const days = Math.floor(deltaMs / MS_PER_DAY)
  return days === 1 ? "1 day ago" : `${days} days ago`
}

/** Same anchor order as {@link formatFreshListingRecencyLabel}. */
export function resolveListingRecencyAnchorIso(
  changeHistoryStatusModificationIso: string | null | undefined,
  statusChangeTimestamp: string | null | undefined,
  onMarketDate: string | null | undefined
): string | null {
  const statusTs = statusChangeTimestamp?.trim()
  if (statusTs && !Number.isNaN(new Date(statusTs).getTime())) {
    return statusTs
  }
  const historyTs = changeHistoryStatusModificationIso?.trim()
  if (historyTs && !Number.isNaN(new Date(historyTs).getTime())) {
    return historyTs
  }
  const marketTs = onMarketDate?.trim()
  if (marketTs && !Number.isNaN(new Date(marketTs).getTime())) {
    return marketTs
  }
  return null
}

/**
 * Whole days since {@link resolveListingRecencyAnchorIso} — matches the day
 * count implied by {@link formatFreshListingRelativeLabel} (“N day(s) ago”).
 */
export function getListingDaysOnMarketFromRecencyAnchor(
  anchorIso: string | null | undefined,
  now: Date = new Date()
): number | undefined {
  if (!anchorIso) return undefined
  const anchor = new Date(anchorIso)
  if (Number.isNaN(anchor.getTime())) return undefined
  const deltaMs = now.getTime() - anchor.getTime()
  if (deltaMs < 0) return 0
  return Math.floor(deltaMs / MS_PER_DAY)
}

/**
 * Min / hr / days ago for the fresh-listing chip.
 *
 * Anchor order: `Computed.StatusChangeTimestamp`, then status-related `ChangeHistory`
 * rows, then `OnMarketDate`. Caller should still gate eligibility with
 * `isNewListingWithinDays(OnMarketDate, …)`.
 */
export function formatFreshListingRecencyLabel(
  changeHistoryStatusModificationIso: string | null | undefined,
  statusChangeTimestamp: string | null | undefined,
  onMarketDate: string | null | undefined,
  now: Date
): string {
  const anchor = resolveListingRecencyAnchorIso(
    changeHistoryStatusModificationIso,
    statusChangeTimestamp,
    onMarketDate
  )
  return formatFreshListingRelativeLabel(anchor, now)
}

export function signedPriceChangeAmountForDisplay(
  priceChangeType: string | null | undefined,
  priceChangeAmount: number | null | undefined
): number | null {
  if (priceChangeAmount == null || priceChangeAmount === 0) return null
  if (priceChangeType !== "Reduction") return priceChangeAmount
  return priceChangeAmount > 0 ? -priceChangeAmount : priceChangeAmount
}

export type IsPriceCutBannerVisibleOptions = {
  /**
   * When `false`, any non-zero `Reduction` is eligible (no 14-day TTL).
   * LDP (`PropertyStatusBanner`) intentionally passes `false` so chips do not
   * expire after 14 days — confirmed product choice for BPP-1733. Default
   * (`true` / omitted) keeps the 14-day window for search property cards when
   * a RESO timestamp is present.
   */
  applyRecencyWindow?: boolean
}

export function isPriceCutBannerVisible(
  priceChangeType: string | null | undefined,
  priceChangeAmount: number | null | undefined,
  priceChangeTimestamp: string | null | undefined,
  now: Date,
  options?: IsPriceCutBannerVisibleOptions
): boolean {
  if (priceChangeType !== "Reduction") return false
  if (priceChangeAmount == null || priceChangeAmount === 0) return false
  if (options?.applyRecencyWindow === false) return true
  if (!priceChangeTimestamp) return true
  const ts = new Date(priceChangeTimestamp)
  if (Number.isNaN(ts.getTime())) return true
  const windowStart = new Date(
    now.getTime() - PRICE_CUT_BADGE_MAX_DAYS * MS_PER_DAY
  )
  return ts >= windowStart && ts <= now
}

/** Intentionally never true: we do not show price-increase chips on listing banners. */
export function isPriceIncreaseBannerVisible(
  _priceChangeType: string | null | undefined,
  _priceChangeAmount: number | null | undefined
): boolean {
  return false
}

/** `M/D` in listing timezone when price changed; empty if invalid or missing. */
export function formatPriceChangeMonthDay(
  priceChangeTimestamp: string | null | undefined,
  propertyTimeZoneName: string | null | undefined
): string {
  if (!priceChangeTimestamp) return ""
  const tz = propertyTimeZoneName || "America/Chicago"
  const d = new Date(priceChangeTimestamp)
  if (Number.isNaN(d.getTime())) return ""
  return new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    month: "numeric",
    day: "numeric",
  }).format(d)
}

/** `(M/D)` in listing timezone, for price-cut chip; empty if invalid. */
export function formatPriceChangeMonthDayParen(
  priceChangeTimestamp: string | null | undefined,
  propertyTimeZoneName: string | null | undefined
): string {
  const md = formatPriceChangeMonthDay(
    priceChangeTimestamp,
    propertyTimeZoneName
  )
  return md ? ` (${md})` : ""
}

/** RESO-style values that show the yellow “contract” pill on property cards. */
export const CONTRACT_STANDARD_STATUSES = [
  "Pending",
  "Under Contract",
  "Active Under Contract",
] as const

export type ContractStandardStatus = (typeof CONTRACT_STANDARD_STATUSES)[number]

const contractSet = new Set<string>(CONTRACT_STANDARD_STATUSES)

export function isContractStandardStatus(
  standardStatus: string | null | undefined
): standardStatus is ContractStandardStatus {
  return standardStatus != null && contractSet.has(standardStatus)
}

/**
 * Display text for contract-status pills.
 * When `useMlsStatus` is true and `mlsStatus` is non-empty, prefer MlsStatus for
 * Pending / Under Contract / Active Under Contract.
 * When override is on for a contract status but MlsStatus is missing, returns
 * `null` so callers can keep their friendly base label (e.g. "Under Contract").
 * Otherwise returns StandardStatus.
 */
export function resolveContractStatusDisplayText(
  standardStatus: string | null | undefined,
  mlsStatus: string | null | undefined,
  useMlsStatus: boolean
): string | null | undefined {
  if (!isContractStandardStatus(standardStatus)) {
    return standardStatus
  }
  if (!useMlsStatus) {
    return standardStatus
  }
  if (mlsStatus != null && mlsStatus.trim() !== "") {
    return mlsStatus
  }
  return null
}

/**
 * NWMLS has no "Coming Soon" period — it is "First Look", and the search API
 * relabels `StandardStatus` to "First Look" for nwmls. Same pre-market phase.
 * The banner date suffix is {@link resolveComingSoonBannerDateParen}.
 */
export function isFirstLookStandardStatus(
  standardStatus: string | null | undefined
): boolean {
  return standardStatus === "First Look"
}

/**
 * @deprecated Use `isComingSoonListingStatus` (or `isComingSoonListing(property)`) from `@/utils/marketStatus`.
 *
 * True for either spelling of the pre-market phase. Every banner rule (date
 * gate, fresh-chip suppression, chip color) applies to both — use
 * {@link resolveComingSoonDisplayLabel} for the user-facing text.
 */
export function isComingSoonStandardStatus(
  standardStatus: string | null | undefined
): boolean {
  return isComingSoonListingStatus(standardStatus)
}

/** Chip / pill text: "First Look" for nwmls, "Coming Soon" everywhere else. */
export function resolveComingSoonDisplayLabel(
  standardStatus: string | null | undefined
): string {
  return isFirstLookStandardStatus(standardStatus)
    ? "First Look"
    : "Coming Soon"
}

/**
 * Whichever of `OnMarketDate` / `ActivationDate` is later, when both are valid.
 * `OnMarketDate` is when the record was entered in the MLS; `ActivationDate` is
 * when a Coming Soon / First Look listing actually goes live to consumers, and
 * can be set well after `OnMarketDate`. Preferring the later date keeps the
 * banner (and its displayed date) visible through the real go-live date
 * instead of disappearing the moment the earlier `OnMarketDate` passes.
 */
export function resolveComingSoonDisplayDate(
  onMarketDate: string | null | undefined,
  activationDate: string | null | undefined
): string | null {
  const onMarket = onMarketDate?.trim()
  const onMarketValid =
    onMarket && !Number.isNaN(new Date(onMarket).getTime()) ? onMarket : null
  const activation = activationDate?.trim()
  const activationValid =
    activation && !Number.isNaN(new Date(activation).getTime())
      ? activation
      : null
  if (onMarketValid && activationValid) {
    return new Date(activationValid) > new Date(onMarketValid)
      ? activationValid
      : onMarketValid
  }
  return onMarketValid ?? activationValid
}

/**
 * True when `dateIso` is today (UTC calendar day) or later. `OnMarketDate` /
 * `ActivationDate` are date-only MLS fields (typically midnight UTC); comparing
 * by UTC calendar day (rather than converting to a local/property timezone)
 * avoids shifting the date to the prior day for negative UTC offsets.
 */
function isOnOrAfterStartOfToday(dateIso: string, now: Date): boolean {
  const parsed = new Date(dateIso)
  if (Number.isNaN(parsed.getTime())) return false
  const parsedUtcDay = Date.UTC(
    parsed.getUTCFullYear(),
    parsed.getUTCMonth(),
    parsed.getUTCDate()
  )
  const nowUtcDay = Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate()
  )
  return parsedUtcDay >= nowUtcDay
}

/**
 * Coming Soon banner (SRP card chip and LDP status pill): requires
 * `StandardStatus === "Coming Soon"` **and** a resolvable date (later of
 * `OnMarketDate` / `ActivationDate`, see {@link resolveComingSoonDisplayDate})
 * that is today or in the future. Past-dated Coming Soon listings (stale MLS
 * data) should not show the banner.
 *
 * `statusOnly` skips the date check for tenants whose feed has no go-live date
 * (see `shouldShowComingSoonOnStatusOnly`).
 */
export function isComingSoonBannerVisible(
  standardStatus: string | null | undefined,
  onMarketDate: string | null | undefined,
  activationDate: string | null | undefined,
  now: Date = new Date(),
  options?: { statusOnly?: boolean }
): boolean {
  if (!isComingSoonStandardStatus(standardStatus)) return false
  if (options?.statusOnly) return true
  const resolved = resolveComingSoonDisplayDate(onMarketDate, activationDate)
  if (!resolved) return false
  return isOnOrAfterStartOfToday(resolved, now)
}

/**
 * `(M/D)` from the later of `OnMarketDate` / `ActivationDate` (see
 * {@link resolveComingSoonDisplayDate}), using UTC calendar
 * date parts (not a timezone conversion) since these are date-only MLS fields —
 * matches {@link isOnOrAfterStartOfToday} so the displayed date and the
 * past/future gate never disagree.
 */
export function formatComingSoonDateParen(
  onMarketDate: string | null | undefined,
  activationDate: string | null | undefined
): string {
  const resolved = resolveComingSoonDisplayDate(onMarketDate, activationDate)
  if (!resolved) return ""
  const d = new Date(resolved)
  if (Number.isNaN(d.getTime())) return ""
  return ` (${d.getUTCMonth() + 1}/${d.getUTCDate()})`
}

/** First Look, status-only, and hideDate omit the `(M/D)` suffix. */
export function resolveComingSoonBannerDateParen(
  standardStatus: string | null | undefined,
  onMarketDate: string | null | undefined,
  activationDate: string | null | undefined,
  statusOnly = false,
  hideDate = false
): string {
  if (statusOnly || hideDate || isFirstLookStandardStatus(standardStatus))
    return ""
  return formatComingSoonDateParen(onMarketDate, activationDate)
}

/**
 * @deprecated Use `CLOSED_LISTING_STANDARD_STATUSES` / `isClosedListingStatus` from `@/utils/marketStatus`.
 * Note: this list is `Closed` only, while marketStatus also accepts `Sold`.
 *
 * RESO `StandardStatus` values treated as closed for LDP sold summary and related UI.
 * Extend this tuple (e.g. add `"Sold"`) if MLS feeds require it — do not scatter string literals.
 */
export const CLOSED_STANDARD_STATUSES = ["Closed"] as const

export type ClosedStandardStatus = (typeof CLOSED_STANDARD_STATUSES)[number]

const closedStandardStatusSet = new Set<string>(CLOSED_STANDARD_STATUSES)

/**
 * @deprecated Use `isClosedListingStatus` from `@/utils/marketStatus`.
 * Note: this one rejects `Sold`; marketStatus accepts it.
 */
export function isClosedStandardStatus(
  standardStatus: string | null | undefined
): standardStatus is ClosedStandardStatus {
  return standardStatus != null && closedStandardStatusSet.has(standardStatus)
}

/**
 * Closed or sold MLS rows — not eligible for active-listing thumbnail chips (price cut, open house, etc.).
 * @deprecated Use `isClosedListingStatus` (or `isClosedListing(property)`) from `@/utils/marketStatus`.
 */
export function isClosedOrSoldStandardStatus(
  standardStatus: string | null | undefined
): boolean {
  return isClosedListingStatus(standardStatus)
}

/**
 * Whether the Favorite CTA should render for a listing (LDP header / SRP card).
 * Hides for off-market rows, missing listing ids, and Closed/Sold MLS status.
 * Does not change `isOnMarket` / `isOffMarket` semantics used elsewhere.
 */
export function shouldShowFavoriteCta(input: {
  listingId: string | null | undefined
  isOffMarket?: boolean
  standardStatus: string | null | undefined
}): boolean {
  if (input.isOffMarket) return false
  if (!input.listingId) return false
  if (isClosedOrSoldStandardStatus(input.standardStatus)) return false
  return true
}

/**
 * `Sold M/D/YYYY` chip label from RESO `Listing.Dates.CloseDate`, e.g. `Sold 7/26/2026`.
 * Returns the bare label (no date) when `closeDate` is missing/invalid so the chip still renders.
 * `label` is the tenant's closed-status word (`resolveClosedStatusLabel`), e.g. `Closed 7/26/2026`.
 *
 * `CloseDate` is a RESO **date-only** field (e.g. `"2026-07-26"`, no time/offset).
 * Deliberately does NOT route through `new Date(...)` + `Intl.DateTimeFormat({ timeZone })` —
 * date-only strings parse as UTC midnight, and reformatting that in any US timezone (all
 * behind UTC) rolls the calendar day back by one. Parsing the `YYYY-MM-DD` components
 * directly avoids any timezone conversion entirely.
 */
export function formatSoldDateLabel(
  closeDate: string | null | undefined,
  label: ClosedStatusLabel = "Sold"
): string {
  if (!closeDate) return label
  const match = closeDate.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return label
  const [, year, month, day] = match as [string, string, string, string]
  const monthNum = Number(month)
  const dayNum = Number(day)
  const yearNum = Number(year)
  if (!monthNum || !dayNum || !yearNum) return label
  return `${label} ${monthNum}/${dayNum}/${yearNum}`
}

/**
 * True when the desktop `PropertyImages` thumbnail grid would be empty (`Images.slice(1)`).
 */
export function listingHasNoGalleryThumbnails(
  images: string[] | null | undefined
): boolean {
  return (images ?? []).slice(1).length === 0
}

/** LDP “sold summary” slot: closed status and no extra gallery images under the hero. */
export function shouldShowPropertySoldBlock(
  standardStatus: string | null | undefined,
  images: string[] | null | undefined
): boolean {
  return (
    isClosedStandardStatus(standardStatus) &&
    listingHasNoGalleryThumbnails(images)
  )
}

/** Whether `onMarketDate` falls within the last `days` days ending at `now` (inclusive). */
export function isNewListingWithinDays(
  onMarketDate: string | null | undefined,
  now: Date,
  days: number = LISTING_FRESH_BADGE_MAX_DAYS
): boolean {
  if (!onMarketDate) return false
  const windowStart = new Date(now.getTime() - days * MS_PER_DAY)
  const onMarket = new Date(onMarketDate)
  return onMarket >= windowStart && onMarket <= now
}

export function hasUpcomingOpenHouseFromEarliestStart(
  openHousesEarliestStartTime: string | null | undefined,
  now: Date,
  openHousesEarliestEndTime?: string | null | undefined,
  propertyTimeZoneName?: string | null
): boolean {
  if (!openHousesEarliestStartTime?.trim()) return false
  const hasEnd = Boolean(openHousesEarliestEndTime?.trim())
  return isOpenHouseActiveOrUpcoming(
    {
      OpenHouseStartTime: openHousesEarliestStartTime,
      OpenHouseEndTime: hasEnd ? openHousesEarliestEndTime : undefined,
    },
    now,
    hasEnd
      ? undefined
      : {
          propertyTimeZoneName,
          assumeEndOfLocalDayWhenEndMissing: true,
        }
  )
}

export type ResolveSearchCardOpenHouseBannerInput = {
  standardStatus?: string | null
  openHouses?: OpenHouseLike[] | null
  openHousesEarliestStartTime?: string | null
  openHousesEarliestEndTime?: string | null
  propertyTimeZoneName?: string | null
  additionalFutureOpenHouseCount?: number
  now?: Date
}

export type ResolveSearchCardOpenHouseBannerResult = {
  line: string
  show: boolean
  additionalFutureOpenHouseCount?: number
}

/** SRP / card overlay: prefer `OpenHouses[]` when present; fall back to Computed earliest times. */
export function resolveSearchCardOpenHouseBanner(
  input: ResolveSearchCardOpenHouseBannerInput
): ResolveSearchCardOpenHouseBannerResult {
  if (!shouldShowOpenHousesForStatus(input.standardStatus)) {
    return { line: "", show: false }
  }

  const now = input.now ?? new Date()
  const tz = input.propertyTimeZoneName

  const fromArray = filterConsumerOpenHouses(input.openHouses)
  if (fromArray.length > 0) {
    const tzOptions: GetOpenHouseVisibilityCutoffOptions = { propertyTimeZoneName: tz }
    const nextOpenHouse = getNextFutureOpenHouseForBanner(fromArray, now, tzOptions)
    if (!nextOpenHouse) return { line: "", show: false }
    const totalFuture = countFutureOpenHousesForBanner(fromArray, now, tzOptions)
    const additional =
      totalFuture > 1 ? totalFuture - 1 : undefined
    const line = formatOpenHouseBannerLabel(
      nextOpenHouse.OpenHouseStartTime || "",
      nextOpenHouse.OpenHouseEndTime ||
        nextOpenHouse.OpenHouseStartTime ||
        "",
      tz,
      additional != null ? { additionalFutureOpenHouseCount: additional } : undefined
    )
    return {
      line,
      show: Boolean(line),
      additionalFutureOpenHouseCount: additional,
    }
  }

  if (
    !hasUpcomingOpenHouseFromEarliestStart(
      input.openHousesEarliestStartTime,
      now,
      input.openHousesEarliestEndTime,
      tz
    )
  ) {
    return { line: "", show: false }
  }

  const line = formatOpenHouseBannerLabel(
    input.openHousesEarliestStartTime || "",
    input.openHousesEarliestEndTime ||
      input.openHousesEarliestStartTime ||
      "",
    tz,
    input.additionalFutureOpenHouseCount != null &&
      input.additionalFutureOpenHouseCount > 0
      ? { additionalFutureOpenHouseCount: input.additionalFutureOpenHouseCount }
      : undefined
  )
  return { line, show: Boolean(line) }
}

export function shouldShowOpenHousesForStatus(
  standardStatus: string | null | undefined
): boolean {
  return standardStatus !== "Pending"
}

function hasUpcomingOpenHouseForSearchCard(
  input: PropertyCardStatusBannerVisibilityInput,
  now: Date
): boolean {
  const fromArray = filterConsumerOpenHouses(input.openHouses)
  if (fromArray.length > 0) {
    return (
      countFutureOpenHousesForBanner(fromArray, now, {
        propertyTimeZoneName: input.propertyTimeZoneName,
      }) > 0
    )
  }
  return hasUpcomingOpenHouseFromEarliestStart(
    input.openHousesEarliestStartTime,
    now,
    input.openHousesEarliestEndTime,
    input.propertyTimeZoneName
  )
}

export type PropertyCardStatusBannerVisibilityInput = {
  onMarketDate: string | null | undefined
  priceChangeAmount: number | null | undefined
  priceChangeType: string | null | undefined
  priceChangeTimestamp: string | null | undefined
  openHouses?: OpenHouseLike[] | null
  openHousesEarliestStartTime: string | null | undefined
  openHousesEarliestEndTime?: string | null | undefined
  propertyTimeZoneName?: string | null
  standardStatus: string | null | undefined
  activationDate: string | null | undefined
  /** Tenant gates Coming Soon on status alone (`shouldShowComingSoonOnStatusOnly`). */
  comingSoonStatusOnly?: boolean
}

/** Thumbnail / map card overlay: contract / Coming Soon (+ open house), else engagement chips. */
export function shouldShowPropertyCardThumbnailBanner(
  input: PropertyCardStatusBannerVisibilityInput,
  now: Date = new Date()
): boolean {
  if (isClosedOrSoldStandardStatus(input.standardStatus)) return true
  if (isContractStandardStatus(input.standardStatus)) return true
  if (
    isComingSoonBannerVisible(
      input.standardStatus,
      input.onMarketDate,
      input.activationDate,
      now,
      { statusOnly: input.comingSoonStatusOnly }
    )
  )
    return true
  if (
    isNewListingWithinDays(
      input.onMarketDate,
      now,
      LISTING_FRESH_BADGE_MAX_DAYS
    )
  )
    return true
  if (
    isPriceCutBannerVisible(
      input.priceChangeType,
      input.priceChangeAmount,
      input.priceChangeTimestamp,
      now
    )
  )
    return true
  if (hasUpcomingOpenHouseForSearchCard(input, now)) return true
  return false
}

/** True when the thumbnail overlay should render any property-card status chip. */
export function shouldShowPropertyCardStatusBanner(
  input: PropertyCardStatusBannerVisibilityInput,
  now: Date = new Date()
): boolean {
  return shouldShowPropertyCardThumbnailBanner(input, now)
}
